// Onboarding Pipeline (Admin) — new retailer pipeline.
//
// Source-of-truth: /var/www/kitchenos/admin-standalone.html
//   page section:    line 1351 (#page-onboardings)
//   kanban stages:   line 3426-3432 (Lead → Contacted → Demo Given → Signed Up → Live)
//   add lead modal:  line 1596-1612
//
// Backend wiring
// --------------
//   GET   /api/admin/onboardings           server.js:1056 — list of in-progress retailer setups
//   GET   /api/account-requests/all        modules/account_requests.js:226 — admin list of signup requests
//   PATCH /api/account-requests/:id        modules/account_requests.js:232 — admin status update
//                                          (statuses: pending, contacted, account-opened, declined)
//
// Mapping
// -------
// Spec calls for three tabs:
//   Account Requests   →  GET /api/account-requests/all where status === "pending"
//   In Progress        →  GET /api/admin/onboardings (records in the onboarding dir)
//                          OR account requests where status === "contacted"|"account-opened"
//   Complete           →  account requests where status === "account-opened" + retailer record exists
//
// We hit BOTH endpoints, then partition client-side so each tab has the right
// shape. Approve → PATCH account-request to "account-opened" (admin-side).
// Reject  → PATCH account-request to "declined" with optional reason.
//
// Spec also mentions `POST /api/admin/onboarding/approve` which doesn't exist;
// we'll try that first, fall back to the PATCH endpoint on 404/501.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type TabKey = "requests" | "in-progress" | "complete";

type Source = "website" | "referral" | "direct" | "unknown";

interface BusinessAddress {
  line1?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
}

interface OnboardingItem {
  id: string;
  retailerName: string;
  email: string;
  phone: string;
  address: BusinessAddress;
  signupDate: string;
  source: Source;
  notes: string;
  status: "pending" | "contacted" | "account-opened" | "declined";
  /** Sequence checklist for in-progress items. */
  checklist?: SetupChecklist;
}

interface SetupChecklist {
  stripeConnected: boolean;
  twilioAssigned: boolean;
  firstSupplierConnected: boolean;
  firstCustomerAdded: boolean;
}

/** Shape from /api/account-requests/all. */
interface ServerAccountRequest {
  id: string;
  status?: "pending" | "contacted" | "account-opened" | "declined";
  createdAt?: string;
  contactedAt?: string;
  accountOpenedAt?: string;
  source?: Source;
  notes?: string;
  retailer?: {
    businessName?: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: BusinessAddress | string;
  };
  supplier?: { id?: string; name?: string };
}

/** Shape from /api/admin/onboardings. */
interface ServerOnboarding {
  id: string;
  businessName?: string;
  email?: string;
  phone?: string;
  address?: BusinessAddress | string;
  createdAt?: string;
  source?: Source;
  notes?: string;
  stripeConnected?: boolean;
  twilioNumber?: string;
  suppliersConnected?: number;
  customersAdded?: number;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatAddress(addr: BusinessAddress | string | undefined): string {
  if (addr == null) return "—";
  if (typeof addr === "string") return addr.trim() || "—";
  const parts: string[] = [];
  for (const key of ["line1", "line2", "city", "county", "postcode"] as const) {
    const v = addr[key];
    if (typeof v === "string" && v.trim()) parts.push(v.trim());
  }
  return parts.length > 0 ? parts.join(", ") : "—";
}

function isAddress(value: unknown): value is BusinessAddress {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ── Demo fallback ────────────────────────────────────────────────────────────

const DEMO_ITEMS: OnboardingItem[] = [
  {
    id: "req_001",
    retailerName: "Leeds Kitchen Studio",
    email: "owner@leedskitchens.co.uk",
    phone: "0113 555 0102",
    address: { line1: "12 Hyde Park Rd", city: "Leeds", postcode: "LS6 1AA" },
    signupDate: "2026-05-08T09:14:00Z",
    source: "website",
    notes: "Saw the demo at K&B Birmingham. Wants to onboard before June.",
    status: "pending",
  },
  {
    id: "req_002",
    retailerName: "Sheffield Cucina",
    email: "alex@sheffcucina.co.uk",
    phone: "0114 555 0188",
    address: { line1: "44 Ecclesall Rd", city: "Sheffield", postcode: "S11 8PG" },
    signupDate: "2026-05-07T15:22:00Z",
    source: "referral",
    notes: "Referred by SJB.",
    status: "pending",
  },
  {
    id: "req_003",
    retailerName: "Bolton Kitchen Co",
    email: "info@boltonkitchenco.uk",
    phone: "01204 555 010",
    address: { line1: "8 Bradshawgate", city: "Bolton", postcode: "BL1 1DG" },
    signupDate: "2026-04-30T11:00:00Z",
    source: "direct",
    notes: "Cold inbound; wants TKC + PWS only.",
    status: "contacted",
    checklist: {
      stripeConnected: true,
      twilioAssigned: true,
      firstSupplierConnected: true,
      firstCustomerAdded: false,
    },
  },
  {
    id: "req_004",
    retailerName: "Manchester Cucina",
    email: "ben@manccucina.co.uk",
    phone: "0161 555 0142",
    address: { line1: "27 King St", city: "Manchester", postcode: "M2 6AA" },
    signupDate: "2026-04-12T10:00:00Z",
    source: "referral",
    notes: "",
    status: "contacted",
    checklist: {
      stripeConnected: true,
      twilioAssigned: false,
      firstSupplierConnected: false,
      firstCustomerAdded: false,
    },
  },
  {
    id: "req_005",
    retailerName: "SJB Trade Kitchens",
    email: "steve@sjbtrade.co.uk",
    phone: "0161 555 0001",
    address: { line1: "1 Trade Park", city: "Oldham", postcode: "OL1 1AA" },
    signupDate: "2025-01-12T09:00:00Z",
    source: "direct",
    notes: "Flagship — founder's own studio.",
    status: "account-opened",
    checklist: {
      stripeConnected: true,
      twilioAssigned: true,
      firstSupplierConnected: true,
      firstCustomerAdded: true,
    },
  },
];

// ── Backend ──────────────────────────────────────────────────────────────────

function normaliseAccountRequest(r: ServerAccountRequest): OnboardingItem {
  const retailer = r.retailer ?? {};
  const addr = isAddress(retailer.address) ? retailer.address : { line1: typeof retailer.address === "string" ? retailer.address : "" };
  return {
    id: r.id,
    retailerName: retailer.businessName || retailer.contactName || "(unnamed)",
    email: retailer.email || "",
    phone: retailer.phone || "",
    address: addr,
    signupDate: r.createdAt || "",
    source: r.source || "unknown",
    notes: r.notes || "",
    status: r.status ?? "pending",
  };
}

function normaliseOnboardingRecord(o: ServerOnboarding): OnboardingItem {
  const addr = isAddress(o.address) ? o.address : { line1: typeof o.address === "string" ? o.address : "" };
  return {
    id: o.id,
    retailerName: o.businessName || "(unnamed)",
    email: o.email || "",
    phone: o.phone || "",
    address: addr,
    signupDate: o.createdAt || "",
    source: o.source || "unknown",
    notes: o.notes || "",
    status: "contacted",
    checklist: {
      stripeConnected: !!o.stripeConnected,
      twilioAssigned: typeof o.twilioNumber === "string" && o.twilioNumber.length > 0,
      firstSupplierConnected: (o.suppliersConnected ?? 0) > 0,
      firstCustomerAdded: (o.customersAdded ?? 0) > 0,
    },
  };
}

interface LoadResult {
  items: OnboardingItem[];
  isDemo: boolean;
}

async function loadOnboarding(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<LoadResult> {
  const [reqRes, obRes] = await Promise.allSettled([
    ctx.api<{ ok: boolean; requests: ServerAccountRequest[] }>(
      "/account-requests/all",
      { signal },
    ),
    ctx.api<ServerOnboarding[]>("/admin/onboardings", { signal }),
  ]);

  if (signal.aborted) {
    throw new DOMException("aborted", "AbortError");
  }

  const isMissing = (r: PromiseSettledResult<unknown>): boolean =>
    r.status === "rejected" &&
    r.reason instanceof ApiError &&
    (r.reason.status === 404 || r.reason.status === 501);

  const items: OnboardingItem[] = [];

  if (reqRes.status === "fulfilled") {
    for (const r of reqRes.value.requests ?? []) items.push(normaliseAccountRequest(r));
  } else if (!isMissing(reqRes)) {
    // Non-404 error → throw to be surfaced.
    throw reqRes.reason;
  }

  if (obRes.status === "fulfilled") {
    for (const o of obRes.value ?? []) items.push(normaliseOnboardingRecord(o));
  } else if (!isMissing(obRes)) {
    throw obRes.reason;
  }

  if (items.length === 0) {
    return { items: DEMO_ITEMS, isDemo: true };
  }
  return { items, isDemo: reqRes.status !== "fulfilled" || obRes.status !== "fulfilled" };
}

async function approveItem(ctx: PageContext, item: OnboardingItem): Promise<void> {
  // Spec says POST /api/admin/onboarding/approve — try that first, fall back
  // to the PATCH endpoint on the account-requests router.
  try {
    await ctx.api("/admin/onboarding/approve", {
      method: "POST",
      body: JSON.stringify({ id: item.id }),
    });
    return;
  } catch (err) {
    if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 501)) {
      throw err;
    }
  }
  await ctx.api(`/account-requests/${encodeURIComponent(item.id)}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "account-opened" }),
  });
}

async function rejectItem(ctx: PageContext, item: OnboardingItem, reason: string): Promise<void> {
  try {
    await ctx.api("/admin/onboarding/reject", {
      method: "POST",
      body: JSON.stringify({ id: item.id, reason }),
    });
    return;
  } catch (err) {
    if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 501)) {
      throw err;
    }
  }
  await ctx.api(`/account-requests/${encodeURIComponent(item.id)}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "declined", reason }),
  });
}

// ── Shell ────────────────────────────────────────────────────────────────────

interface ShellRefs {
  banner: HTMLElement;
  tabs: HTMLElement;
  counters: Record<TabKey, HTMLElement>;
  panes: Record<TabKey, HTMLElement>;
  refreshBtn: HTMLButtonElement;
}

function renderShell(container: HTMLElement): ShellRefs {
  container.innerHTML = `
    <section class="aon-page" aria-labelledby="aon-title">
      <header class="aon-head">
        <div>
          <h1 id="aon-title" class="aon-title">Onboarding Pipeline</h1>
          <p class="aon-sub">New retailer signups and in-progress setups. Approve to flip them live.</p>
        </div>
        <div class="aon-head-actions">
          <button class="adm-btn-ghost adm-btn" data-role="refresh" type="button">Refresh</button>
        </div>
      </header>

      <div class="aon-banner" data-role="banner" hidden></div>

      <nav class="aon-tabs" role="tablist" data-role="tabs">
        <button class="aon-tab is-active" role="tab" data-tab="requests" aria-selected="true">
          Account Requests <span class="aon-tab-count" data-count="requests">0</span>
        </button>
        <button class="aon-tab" role="tab" data-tab="in-progress" aria-selected="false">
          In Progress <span class="aon-tab-count" data-count="in-progress">0</span>
        </button>
        <button class="aon-tab" role="tab" data-tab="complete" aria-selected="false">
          Complete <span class="aon-tab-count" data-count="complete">0</span>
        </button>
      </nav>

      <div class="aon-panes">
        <div class="aon-pane is-active" role="tabpanel" data-pane="requests">
          <div class="adm-loading">Loading…</div>
        </div>
        <div class="aon-pane" role="tabpanel" data-pane="in-progress" hidden>
          <div class="adm-loading">Loading…</div>
        </div>
        <div class="aon-pane" role="tabpanel" data-pane="complete" hidden>
          <div class="adm-loading">Loading…</div>
        </div>
      </div>
    </section>
  `;

  const counters: Record<TabKey, HTMLElement> = {
    requests: container.querySelector<HTMLElement>("[data-count='requests']")!,
    "in-progress": container.querySelector<HTMLElement>("[data-count='in-progress']")!,
    complete: container.querySelector<HTMLElement>("[data-count='complete']")!,
  };
  const panes: Record<TabKey, HTMLElement> = {
    requests: container.querySelector<HTMLElement>("[data-pane='requests']")!,
    "in-progress": container.querySelector<HTMLElement>("[data-pane='in-progress']")!,
    complete: container.querySelector<HTMLElement>("[data-pane='complete']")!,
  };

  return {
    banner: container.querySelector<HTMLElement>("[data-role='banner']")!,
    tabs: container.querySelector<HTMLElement>("[data-role='tabs']")!,
    counters,
    panes,
    refreshBtn: container.querySelector<HTMLButtonElement>("[data-role='refresh']")!,
  };
}

function partition(items: OnboardingItem[]): Record<TabKey, OnboardingItem[]> {
  return {
    requests: items.filter((i) => i.status === "pending"),
    "in-progress": items.filter((i) => i.status === "contacted"),
    complete: items.filter((i) => i.status === "account-opened"),
  };
}

function renderCard(item: OnboardingItem, tab: TabKey): string {
  const checklist = item.checklist;
  const checklistHtml =
    tab === "in-progress" && checklist
      ? `
        <ul class="aon-checklist" aria-label="Setup checklist">
          ${renderCheck("Stripe connected", checklist.stripeConnected)}
          ${renderCheck("Twilio number assigned", checklist.twilioAssigned)}
          ${renderCheck("First supplier connected", checklist.firstSupplierConnected)}
          ${renderCheck("First customer added", checklist.firstCustomerAdded)}
        </ul>
      `
      : "";

  const actionsHtml =
    tab === "requests"
      ? `
        <div class="aon-actions">
          <button class="adm-btn aon-btn-approve" data-action="approve" data-id="${escapeHtml(item.id)}" type="button">Approve</button>
          <button class="adm-btn-ghost adm-btn aon-btn-reject" data-action="reject" data-id="${escapeHtml(item.id)}" type="button">Reject</button>
        </div>`
      : tab === "in-progress"
      ? `
        <div class="aon-actions">
          <button class="adm-btn aon-btn-approve" data-action="approve" data-id="${escapeHtml(item.id)}" type="button">Mark Complete</button>
        </div>`
      : "";

  return `
    <article class="aon-card" data-id="${escapeHtml(item.id)}">
      <header class="aon-card-head">
        <div>
          <h3 class="aon-card-name">${escapeHtml(item.retailerName)}</h3>
          <p class="aon-card-meta">
            <span>${escapeHtml(item.email || "—")}</span>
            <span>·</span>
            <span>${escapeHtml(item.phone || "—")}</span>
            <span>·</span>
            <span>${escapeHtml(formatAddress(item.address))}</span>
          </p>
        </div>
        <div class="aon-card-side">
          <span class="adm-pill adm-pill-info">${escapeHtml(item.source)}</span>
          <span class="aon-card-date">${escapeHtml(formatDate(item.signupDate))}</span>
        </div>
      </header>
      ${item.notes ? `<p class="aon-card-notes">${escapeHtml(item.notes)}</p>` : ""}
      ${checklistHtml}
      ${actionsHtml}
    </article>
  `;
}

function renderCheck(label: string, done: boolean): string {
  return `
    <li class="aon-check${done ? " is-done" : ""}">
      <span class="aon-check-icon" aria-hidden="true">${done ? "✓" : "○"}</span>
      <span>${escapeHtml(label)}</span>
    </li>
  `;
}

function renderPane(host: HTMLElement, items: OnboardingItem[], tab: TabKey): void {
  if (items.length === 0) {
    const empty =
      tab === "requests"
        ? "No new signup requests."
        : tab === "in-progress"
        ? "No retailers currently in setup."
        : "No completed onboardings yet.";
    host.innerHTML = `<div class="adm-empty">${escapeHtml(empty)}</div>`;
    return;
  }
  host.innerHTML = items.map((i) => renderCard(i, tab)).join("");
}

// ── Reject reason modal ──────────────────────────────────────────────────────

function openRejectModal(
  item: OnboardingItem,
  onSubmit: (reason: string) => Promise<void>,
): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "aon-dialog";
  dialog.innerHTML = `
    <form method="dialog" class="aon-form">
      <h2 class="aon-form-title">Reject signup</h2>
      <p class="aon-form-sub">Rejecting <strong>${escapeHtml(item.retailerName)}</strong>. The reason is recorded against the account request.</p>
      <label class="aon-field">
        <span>Reason</span>
        <textarea class="adm-textarea" name="reason" rows="4" required minlength="3" maxlength="500" placeholder="e.g. duplicate of existing request"></textarea>
      </label>
      <div class="aon-form-err" data-role="form-err" hidden></div>
      <footer class="aon-form-actions">
        <button class="adm-btn-ghost adm-btn" value="cancel" type="submit">Cancel</button>
        <button class="adm-btn adm-btn-danger" value="confirm" type="submit" data-role="confirm">Reject</button>
      </footer>
    </form>
  `;
  document.body.appendChild(dialog);

  const form = dialog.querySelector<HTMLFormElement>(".aon-form")!;
  const err = dialog.querySelector<HTMLElement>("[data-role='form-err']")!;

  form.addEventListener("submit", (ev) => {
    const submitter = (ev as SubmitEvent).submitter as HTMLButtonElement | null;
    if (submitter?.value !== "confirm") return;
    ev.preventDefault();
    err.hidden = true;
    const reason = (form.elements.namedItem("reason") as HTMLTextAreaElement).value.trim();
    if (reason.length < 3) {
      err.textContent = "Please give a reason (3+ characters).";
      err.hidden = false;
      return;
    }
    const confirm = dialog.querySelector<HTMLButtonElement>("[data-role='confirm']")!;
    confirm.disabled = true;
    confirm.textContent = "Rejecting…";
    void onSubmit(reason)
      .then(() => dialog.close("confirm"))
      .catch((e: unknown) => {
        confirm.disabled = false;
        confirm.textContent = "Reject";
        err.textContent = e instanceof Error ? e.message : "Couldn't reject.";
        err.hidden = false;
      });
  });

  dialog.addEventListener("close", () => dialog.remove());

  dialog.showModal();
  dialog.querySelector<HTMLTextAreaElement>("textarea")?.focus();
  return dialog;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const onboardingPage: Page = {
  name: "onboarding",
  label: "Onboarding",
  icon: "🚀",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const refs = renderShell(container);

    let items: OnboardingItem[] = [];
    let currentTab: TabKey = "requests";
    let controller: AbortController | null = null;
    let disposed = false;
    let activeDialog: HTMLDialogElement | null = null;

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        refs.banner.hidden = true;
        refs.banner.textContent = "";
        refs.banner.classList.remove("aon-banner--error");
        return;
      }
      refs.banner.hidden = false;
      refs.banner.textContent = msg;
      refs.banner.classList.toggle("aon-banner--error", kind === "error");
    };

    const renderAll = (): void => {
      const parts = partition(items);
      (Object.keys(parts) as TabKey[]).forEach((tab) => {
        refs.counters[tab].textContent = String(parts[tab].length);
        renderPane(refs.panes[tab], parts[tab], tab);
      });
    };

    const setTab = (next: TabKey): void => {
      currentTab = next;
      refs.tabs.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) => {
        const active = btn.dataset.tab === next;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-selected", String(active));
      });
      (Object.keys(refs.panes) as TabKey[]).forEach((k) => {
        const pane = refs.panes[k];
        const active = k === next;
        pane.classList.toggle("is-active", active);
        pane.hidden = !active;
      });
    };

    const load = async (): Promise<void> => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      for (const pane of Object.values(refs.panes)) {
        pane.innerHTML = `<div class="adm-loading">Loading…</div>`;
      }
      try {
        const result = await loadOnboarding(ctx, signal);
        if (disposed || signal.aborted) return;
        items = result.items;
        renderAll();
        setBanner(
          result.isDemo
            ? "Live onboarding feed unavailable — showing demo seed."
            : null,
          "info",
        );
      } catch (err) {
        if (signal.aborted) return;
        const msg = err instanceof Error ? err.message : "Couldn't load onboarding";
        items = DEMO_ITEMS;
        renderAll();
        setBanner(`${msg} — showing demo seed.`, "error");
        ctx.showToast(msg, "error");
      }
    };

    const onTabsClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("button[data-tab]");
      if (!btn) return;
      const next = btn.dataset.tab as TabKey | undefined;
      if (!next || next === currentTab) return;
      setTab(next);
    };

    const onRefreshClick = (): void => {
      void load();
    };

    const handleApprove = async (item: OnboardingItem): Promise<void> => {
      if (!window.confirm(`Approve ${item.retailerName}? They will be activated as a live retailer.`)) {
        return;
      }
      try {
        await approveItem(ctx, item);
        item.status = "account-opened";
        renderAll();
        ctx.showToast(`Approved ${item.retailerName}`, "success");
      } catch (err) {
        ctx.showToast(
          err instanceof Error ? err.message : "Couldn't approve retailer",
          "error",
        );
      }
    };

    const handleReject = (item: OnboardingItem): void => {
      if (activeDialog) return;
      activeDialog = openRejectModal(item, async (reason) => {
        await rejectItem(ctx, item, reason);
        item.status = "declined";
        items = items.filter((i) => i.status !== "declined");
        renderAll();
        ctx.showToast(`Rejected ${item.retailerName}`, "info");
      });
      activeDialog.addEventListener("close", () => {
        activeDialog = null;
      });
    };

    const onPaneClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("button[data-action]");
      if (!btn) return;
      const id = btn.dataset.id!;
      const item = items.find((i) => i.id === id);
      if (!item) return;
      if (btn.dataset.action === "approve") {
        void handleApprove(item);
      } else if (btn.dataset.action === "reject") {
        handleReject(item);
      }
    };

    refs.tabs.addEventListener("click", onTabsClick);
    refs.refreshBtn.addEventListener("click", onRefreshClick);
    for (const pane of Object.values(refs.panes)) {
      pane.addEventListener("click", onPaneClick);
    }

    void load();

    return (): void => {
      disposed = true;
      controller?.abort();
      controller = null;
      refs.tabs.removeEventListener("click", onTabsClick);
      refs.refreshBtn.removeEventListener("click", onRefreshClick);
      for (const pane of Object.values(refs.panes)) {
        pane.removeEventListener("click", onPaneClick);
      }
      if (activeDialog && activeDialog.open) activeDialog.close();
      activeDialog?.remove();
      activeDialog = null;
    };
  },
};

registerPage(onboardingPage);
